import React, { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery } from "@apollo/client"
import { Alert, Button, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Col } from "reactstrap"
import gql from "graphql-tag"
import Select from "react-select"
import "./Modals.css"
import toTitleCase from "../../Functions/toTitleCase"
import AttachmentViewer from "../../Drivers/Interactions/AttachmentViewer"

const ALL_INCIDENT_PARTIES = gql`
  query AllIncidentParties($incidentId: ID!) {
    allIncidentParties(incidentId: $incidentId, isArchived: false) {
      edges {
        node {
          id
          partyName
          partyType
        }
      }
    }
  }
`

const ALL_INCIDENT_DOCUMENT_TYPES = gql`
  query AllIncidentDocumentTypes($category: String!) {
    allIncidentDocumentTypes(category: $category, isActive: true) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

const MOVE_INCIDENT_DOCUMENT = gql`
  mutation MoveIncidentDocument($input: MoveIncidentDocumentMutationInput!) {
    moveIncidentDocument(input: $input) {
      ok
      errors {
        messages
      }
    }
  }
`

const MoveIncidentDocumentModal = ({ isOpen, onClose, incidentId, documents = [], viewType, refetch, onMoved }) => {
  const [selectedPartyId, setSelectedPartyId] = useState("")
  const [selectedDocTypeIds, setSelectedDocTypeIds] = useState({})
  const [error, setError] = useState(null)
  const [openModal, setOpenModal] = useState("")

  const movingToParty = viewType === "incident"
  const { data: partiesData, loading: partiesLoading } = useQuery(ALL_INCIDENT_PARTIES, {
    variables: { incidentId },
    skip: !isOpen || !movingToParty
  })

  const { data: docTypesData, loading: docTypesLoading } = useQuery(ALL_INCIDENT_DOCUMENT_TYPES, {
    variables: { category: movingToParty ? "PARTY" : "INCIDENT" },
    skip: !isOpen
  })

  const [moveIncidentDocument, { loading: moveLoading }] = useMutation(MOVE_INCIDENT_DOCUMENT)

  useEffect(() => {
    if (isOpen) {
      const initialDocTypeIds = documents.reduce((acc, doc) => {
        acc[doc.id] = doc.documentType?.id || ""
        return acc
      }, {})
      setSelectedPartyId("")
      setSelectedDocTypeIds(initialDocTypeIds)
      setError(null)
    }
  }, [isOpen, documents])

  const partyOptions = useMemo(() =>
    partiesData?.allIncidentParties?.edges?.map(({ node }) => ({
      value: node.id,
      label: `${node.partyName || "Unnamed Party"} (${toTitleCase(node.partyType) || "Unknown Party"})`
    })) ?? [],
    [partiesData]
  )

  const docTypeOptions = useMemo(() =>
    docTypesData?.allIncidentDocumentTypes?.edges?.map(({ node }) => ({
      value: node.id,
      label: node.name
    })) ?? [],
    [docTypesData]
  )

  const handleMove = async () => {
    setError(null)
    try {
      const { data } = await moveIncidentDocument({
        variables: {
          input: {
            documents: documents.map(doc => ({
              incidentDocumentId: doc.id,
              documentTypeId: selectedDocTypeIds[doc.id],
              instanceId: movingToParty ? selectedPartyId : incidentId
            }))
          }
        }
      })
      const result = data?.moveIncidentDocument
      if (result?.ok) {
        refetch?.()
        onMoved?.()
        onClose?.()
        return
      }
      const errorMessages = result?.errors?.flatMap(err => err?.messages || []) || []
      setError(errorMessages.join(" ") || "Move failed.")
    } catch (e) {
      setError(e.message)
    }
  }

  const handleDocTypeChange = (docId, docTypeId) => {
    setSelectedDocTypeIds(prev => ({
      ...prev,
      [docId]: docTypeId
    }))
  }

  const allDocTypesSelected = documents.length > 0 && documents.every(({ id }) => docTypeOptions.some(({ value }) => value === selectedDocTypeIds[id]))
  return (
    <Modal isOpen={isOpen} toggle={onClose} className="move-document-modal">
      <ModalHeader>Move Document</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {movingToParty && (
          <FormGroup>
            <Label>Select Party</Label>
            <Select
              classNamePrefix="move-doc-select"
              options={partyOptions}
              value={partyOptions.find(o => o.value === selectedPartyId) || null}
              onChange={opt => setSelectedPartyId(opt?.value || "")}
              isLoading={partiesLoading}
              isClearable
              placeholder="Select a party"
            />
          </FormGroup>
        )}
        {documents.length > 0 && (
          <div className="mb-3">
            {documents.map((doc, i) => (
              <Row className="d-flex align-items-center mb-2" key={doc.id}>
                <Col xs="5" className="d-flex justify-content-between">
                  <span>{doc.documentType?.typeName || doc.document || doc.name || "Document"}</span>
                  <span><i className="fa fa-arrows-alt" onClick={() => setOpenModal("viewdocument" + i)} title="Preview Document" /></span>
                  {openModal === "viewdocument" + i &&
                    <AttachmentViewer
                      open={true}
                      attachment={doc}
                      handleClose={() => setOpenModal("")}
                    />
                  }
                </Col>
                <Col xs="1" className="text-center">
                  <i className="fa fa-long-arrow-right" aria-hidden="true" />
                </Col>
                <Col xs="6">
                  <Select
                    classNamePrefix="move-doc-select"
                    options={docTypeOptions}
                    value={docTypeOptions.find(o => o.value === selectedDocTypeIds[doc.id]) || null}
                    onChange={opt => handleDocTypeChange(doc.id, opt?.value || "")}
                    isLoading={docTypesLoading}
                    isClearable
                    placeholder="Select a document type"
                  />
                </Col>
              </Row>
            ))}
          </div>
        )}
        {documents.length === 0 && (
          <Alert color="warning">No documents selected.</Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={moveLoading}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleMove} disabled={moveLoading || docTypesLoading || !allDocTypesSelected || (movingToParty && !selectedPartyId)}>
          {moveLoading ? <div className="spinner-border spinner-border-sm" /> : "Move"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default MoveIncidentDocumentModal
